import type { PropertyLifecycle } from '@natalan/capehost-contracts'

/**
 * Where a property is in its life (CAP-1457, CAP-1467): `onboarding` for a
 * locally created property that has no Uplisting listing yet, `live` once
 * linked, `archived` after offboarding. admin-api derives it on every
 * property response; the contracts default an absent value to `live`, and
 * so does this, for a row from an older response or a test fixture.
 */
export function lifecycleOf(property: { lifecycle?: PropertyLifecycle | null }) {
  return property.lifecycle ?? 'live'
}

export function isOnboardingProperty(property: { lifecycle?: PropertyLifecycle | null }) {
  return lifecycleOf(property) === 'onboarding'
}

/**
 * The properties an operational picker (reservations, cleaning, sync,
 * security deposits, tax, supplies) may offer: live ones only. An onboarding
 * property has no Uplisting listing, so nothing to act on yet; an archived one
 * is no longer managed. The pickers read the active list, which admin-api
 * already returns without archived properties, so dropping them here too only
 * keeps that rule true if a picker is ever pointed at another list. The
 * navigation picker in the property header is the one place that keeps both,
 * since their own page is where onboarding and offboarding happen.
 */
export function pickableProperties<
  T extends { lifecycle?: PropertyLifecycle | null; archived?: boolean | null },
>(properties: readonly T[]): T[] {
  return properties.filter(
    property => lifecycleOf(property) === 'live' && property.archived !== true
  )
}
